import { type FormEvent, useId, useState } from "react";
import { ApiError, api, type BillingSummary, type When } from "./api.ts";
import { Modal } from "./Dialog.tsx";
import { FIRST_TRIP } from "./FirstTrip.tsx";
import { ReportThis } from "./ReportThis.tsx";
import { navigate, tripPath } from "./router.ts";
import { Button } from "./souvenir.ts";
import { CopyButton } from "./ui.tsx";

// Starting a trip on the web (features/paying.md, spec §8.1b and §8.1c): its name and, if they're known,
// its first and last days; the AI plans the rest. From a Trip Pass's placeholder ("pass": that pass pays
// for it) or New trip ("plan": the person's plan, within fair use). Or the person asks their AI, which
// uses the pass or the plan by itself. Once it's made, the trip opens.

export type StartMode = { kind: "pass"; pass: string } | { kind: "plan"; billing: BillingSummary };

const TITLE_MAX = 200;

export function StartTrip({
  mode,
  onClose,
  onChanged,
}: {
  mode: StartMode;
  onClose: () => void;
  // What Your trips shows is out of date (the pass was used meanwhile, the plan ended): load it again.
  onChanged: () => void;
}) {
  const nameId = useId();
  const fromId = useId();
  const toId = useId();
  const [title, setTitle] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; trip?: { id: string; title: string } } | null>(null);
  const [copied, setCopied] = useState(false);
  // The pass can't start this trip any more (used, refunded, not this person's, or a plan began): the
  // refusal says so, and Start trip stays off.
  const [spent, setSpent] = useState(false);
  const limit = mode.kind === "plan" ? fairUseLine(mode.billing) : null;

  const dates = (): When | undefined | string => {
    if (!from && !to) return undefined;
    if (!from) return "Add the first day too, or leave both empty.";
    if (!to || to === from) return { precision: "day", day: from };
    if (to < from) return "The last day is before the first.";
    return { precision: "days", start: from, end: to };
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const name = title.trim();
    if (!name) {
      setError({ message: "Give the trip a name." });
      return;
    }
    const when = dates();
    if (typeof when === "string") {
      setError({ message: when });
      return;
    }
    const trip = { title: name, ...(when ? { dates: when } : {}) };
    setBusy(true);
    setError(null);
    try {
      const made = mode.kind === "pass" ? await api.startTripFromPass(mode.pass, trip) : await api.newTrip(trip);
      navigate(tripPath(made.trip.id));
    } catch (err) {
      setBusy(false);
      const message = err instanceof Error ? err.message : "Couldn't start the trip. Try again.";
      const used =
        err instanceof ApiError ? (err.data["trip"] as { id: string; title: string } | undefined) : undefined;
      setError({ message, ...(used ? { trip: used } : {}) });
      // Refused because Your trips is behind (the pass was used or refunded, the plan ended or began):
      // it shows how things are now behind the dialog.
      if (err instanceof ApiError && [402, 404, 409, 410].includes(err.status)) {
        onChanged();
        if (mode.kind === "pass" || err.status === 402) setSpent(true);
      }
    }
  };

  return (
    <Modal
      title={mode.kind === "pass" ? "Start this trip" : "New trip"}
      busy={busy}
      focus={`[id="${nameId}"]`}
      onCancel={onClose}
      className="tm-start-trip"
    >
      {({ describedBy }) => (
        <form className="tm-start-form" onSubmit={submit} noValidate>
          <p id={describedBy} className="callout tm-muted">
            {mode.kind === "pass"
              ? "Your Trip Pass covers it, for up to 5 people, you included. Then your AI plans it with you."
              : `Included in ${mode.billing.plan_name ?? "your plan"}. Then your AI plans it with you.`}
          </p>
          <label className="sv-field" htmlFor={nameId}>
            <span className="sv-field-label">Trip name</span>
            <input
              id={nameId}
              className="sv-field-input"
              value={title}
              maxLength={TITLE_MAX}
              autoComplete="off"
              enterKeyHint="go"
              placeholder="Japan in spring"
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <fieldset className="tm-start-dates">
            <legend className="sv-field-label">Dates, if you know them</legend>
            <div className="tm-start-pair">
              <label className="tm-start-date" htmlFor={fromId}>
                <span className="caption tm-muted">First day</span>
                <input
                  id={fromId}
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    if (to && e.target.value > to) setTo("");
                  }}
                />
              </label>
              <label className="tm-start-date" htmlFor={toId}>
                <span className="caption tm-muted">Last day</span>
                <input
                  id={toId}
                  type="date"
                  value={to}
                  min={from || undefined}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
            </div>
          </fieldset>
          {limit && <p className={`callout${limit.blocked ? " tm-error" : " tm-muted"}`}>{limit.text}</p>}
          {error && (
            <p className="callout tm-error" role="alert">
              {error.message}
              {!error.trip && <ReportThis />}
              {error.trip && (
                <>
                  {" "}
                  <a
                    className="tm-error-link"
                    href={tripPath(error.trip.id)}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0 || !error.trip) return;
                      e.preventDefault();
                      navigate(tripPath(error.trip.id));
                    }}
                  >
                    Open {error.trip.title}
                  </a>
                </>
              )}
            </p>
          )}
          <div className="tm-dialog-actions">
            <Button variant="secondary" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={busy || spent || Boolean(limit?.blocked)}>
              {busy ? "Starting…" : "Start trip"}
            </Button>
          </div>
          <div className="tm-start-ai">
            <CopyButton
              className="tm-start"
              text={FIRST_TRIP}
              variant="quiet"
              size="sm"
              label="Or ask your AI to start it"
              onCopied={() => setCopied(true)}
            />
            {copied && (
              <p className="caption tm-muted" role="status">
                Paste it into your AI. It asks you a few questions, then starts the trip
                {mode.kind === "pass" ? " with this pass." : "."}
              </p>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
}

// What New trip says about fair use (spec §3.2): how many are left when few are, or, at the limit, when
// one more fits. Null while there's room.
export function fairUseLine(billing: BillingSummary): { text: string; blocked: boolean } | null {
  const use = billing.fair_use;
  if (!use) return null;
  const dayLeft = use.per_day.max - use.per_day.used;
  const activeLeft = use.active.max - use.active.used;
  if (!billing.can_create_trip) {
    if (dayLeft <= 0)
      return {
        blocked: true,
        text: `You've started ${use.per_day.used} trips in the last 24 hours, Tripmo's fair-use limit.${
          use.per_day.resets_at ? ` You can start another after ${when(use.per_day.resets_at)}.` : ""
        }`,
      };
    return {
      blocked: true,
      text: `You have ${use.active.used} trips on your plan, Tripmo's fair-use limit. Delete ones you no longer need to start another.`,
    };
  }
  if (dayLeft > 0 && dayLeft <= 3)
    return { blocked: false, text: `${dayLeft} of ${use.per_day.max} new trips left today` };
  if (activeLeft > 0 && activeLeft <= 3)
    return { blocked: false, text: `Room for ${activeLeft} more of ${use.active.max} trips on your plan` };
  return null;
}

// "15:40", or "Mon 15:40" when it isn't today, in the reader's own clock.
function when(iso: string): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return at.toDateString() === new Date().toDateString()
    ? time
    : `${at.toLocaleDateString("en-GB", { weekday: "short" })} ${time}`;
}
